// ジェスチャータブ: 左手 8 スロット + 右手 8 スロット（Neutral 含む）のグリッド。
// スロットのタップはプレビューのみ更新。割り当てはクリップトレイからのドラッグ&ドロップで行う
// （HTML5 D&D はモバイルで非対応のため Pointer Events で自前実装）。
// 「詳細」内に左右コンビネーション表（左 8 × 右 8）。セルは小さいためタップ選択＋下部固定バーで変更する。

import { GESTURE_LABELS_JA, GESTURE_NAMES, type Hand } from "../core/types.ts";
import { store } from "../store/state.ts";
import { el, modal, openMenu } from "./dom.ts";
import { attachPreview, refreshPreview } from "./preview.ts";

const HAND_LABEL: Record<Hand, string> = { left: "左手", right: "右手" };

/** PC幅（2カラムレイアウト、styles.css の900pxブレークポイントと一致）か。 */
function isPcLayout(): boolean {
  return window.matchMedia("(min-width: 900px)").matches;
}

/** 左右共通のクリップ選択ポップアップ。assign は選択されたクリップ index（null=解除）を受け取る。 */
function openClipPicker(title: string, current: number | null, assign: (clipIndex: number | null) => void): void {
  const project = store.project;
  if (!project) return;

  const body = el("div", {});

  body.appendChild(
    el(
      "button",
      {
        class: "pick-item",
        "aria-selected": current === null ? "true" : "false",
        onclick: () => {
          assign(null);
          handle.close();
        },
      },
      el("span", { text: "割り当てなし" }),
      el("span", { class: "sub", text: "既存の表情のまま" }),
    ),
  );

  if (project.clips.length === 0) {
    body.appendChild(
      el("p", { class: "muted", text: "クリップがまだありません。「表情」タブで作成してください。", style: { marginTop: "10px" } }),
    );
  }

  for (const clip of project.clips) {
    body.appendChild(
      el(
        "button",
        {
          class: "pick-item",
          "aria-selected": current === clip.index ? "true" : "false",
          onclick: () => {
            assign(clip.index);
            handle.close();
          },
        },
        el("span", { text: clip.name }),
        el("span", {
          class: "sub",
          text: clip.type === "keyframe" ? `KF ${clip.keyframes?.length ?? 0}枚` : `${clip.shapes.length}個`,
        }),
      ),
    );
  }

  const handle = modal(title, body);
}

function slot(hand: Hand, gesture: number): HTMLElement {
  const clipIndex = store.project?.gestures[hand][gesture] ?? null;
  const clip = store.clipByIndex(clipIndex);
  const p = store.previewing;
  const isPreviewing = p?.kind === "hand" && p.hand === hand && p.gesture === gesture;

  return el(
    "button",
    {
      class: `gesture-slot${clip ? " assigned" : ""}`,
      "aria-pressed": isPreviewing ? "true" : "false",
      "data-hand": hand,
      "data-gesture": String(gesture),
      onclick: () => {
        // タップはプレビューのみ更新する。割り当てはクリップトレイからのドラッグ&ドロップで行う。
        store.setPreviewTarget({ kind: "hand", hand, gesture });
        refreshPreview();
      },
      oncontextmenu: (e: Event) => e.preventDefault(),
    },
    el("span", { class: "g-name", text: `${gesture}. ${GESTURE_LABELS_JA[gesture]}` }),
    el("span", {
      class: `g-clip${clip ? "" : " empty"}`,
      text: clip ? clip.name : "未割り当て",
      title: clip ? clip.name : GESTURE_NAMES[gesture],
    }),
  );
}

/** ドラッグ中に自前実装するポインタ追従。HTML5 D&D はモバイルで非対応のため使わない。 */
const DRAG_THRESHOLD_PX = 8;
/** 長押し確定までの時間。この間に大きく動いたら横スクロール意図とみなしドラッグをキャンセルする。 */
const LONG_PRESS_MS = 350;
/** 長押し確定前に、この距離を超えて動いたらスクロールとみなしキャンセルする。 */
const SCROLL_CANCEL_PX = 10;

function clipChip(clipIndex: number | null, name: string): HTMLElement {
  const chip = el("button", {
    class: `clip-chip${clipIndex === null ? " unassign" : ""}`,
    type: "button",
    text: name,
  });

  /** ドラッグ追従中か（touchmove の preventDefault 判定用。チップ単位で1つ）。 */
  let touchDragActive = false;
  // CSS は touch-action: pan-x pan-y（長押し確定前はブラウザの通常スクロールに任せる）。
  // touch-action はジェスチャ途中で変えても効かないため、長押しでドラッグが確定した後は
  // non-passive な touchmove で preventDefault してスクロール（→ pointercancel）を抑止する。
  chip.addEventListener(
    "touchmove",
    (e) => {
      if (touchDragActive && e.cancelable) e.preventDefault();
    },
    { passive: false },
  );

  chip.addEventListener("pointerdown", (ev) => {
    // 主ボタン以外（右クリック等）や、ボタンが押されていない pointerdown では開始しない
    if (ev.button !== 0 || ev.buttons === 0) return;
    const pointerId = ev.pointerId;
    const startX = ev.clientX;
    const startY = ev.clientY;
    /** マウス操作は横スクロールと誤操作しないため、長押し待ちなしで閾値移動のみでドラッグ開始する。 */
    const isMouse = ev.pointerType === "mouse";
    let dragging = false;
    /** 長押し確定前に指が大きく動いた＝横スクロール意図。以後ドラッグもタップも発生させない。 */
    let canceled = false;
    let ghost: HTMLElement | null = null;
    let hoverSlot: HTMLElement | null = null;
    let longPressTimer: ReturnType<typeof setTimeout> | null = null;

    const clearHover = (): void => {
      hoverSlot?.classList.remove("drop-target");
      hoverSlot = null;
    };

    const beginDrag = (clientX: number, clientY: number): void => {
      dragging = true;
      touchDragActive = true;
      if (trayExpanded) {
        trayExpanded = false;
        store.notify();
      }
      chip.style.touchAction = "none";
      try {
        chip.setPointerCapture(pointerId);
      } catch {
        /* 取得できない環境ではポインタ追従のみで続行 */
      }
      ghost = el("div", { class: "clip-chip-ghost", text: name });
      ghost.style.left = `${clientX}px`;
      ghost.style.top = `${clientY}px`;
      document.body.appendChild(ghost);
    };

    // 1秒未満の長押しでドラッグ追従を開始する（触れた瞬間の即ドラッグは横スクロールと誤操作しやすいため）。
    // マウス操作はスクロール誤操作の懸念がないため、長押し待ちをせず閾値移動のみでドラッグ開始する。
    if (!isMouse) {
      longPressTimer = setTimeout(() => {
        longPressTimer = null;
        if (canceled) return;
        beginDrag(startX, startY);
      }, LONG_PRESS_MS);
    }

    const onMove = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      // ウィンドウ外でボタンを離した等で pointerup を取りこぼした場合は、ここで中断する
      if (e.buttons === 0) {
        abort();
        return;
      }
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;
      if (!dragging) {
        if (canceled) return;
        if (longPressTimer !== null) {
          // 長押し確定前: 大きく動いたらスクロール意図とみなしキャンセル（縦横問わず、ブラウザの通常スクロールに任せる）
          if (Math.hypot(dx, dy) > SCROLL_CANCEL_PX) {
            canceled = true;
            clearTimeout(longPressTimer);
            longPressTimer = null;
          }
          return;
        }
        // 長押し確定後: わずかな移動でドラッグを開始する
        if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        beginDrag(e.clientX, e.clientY);
      }
      if (ghost) {
        ghost.style.left = `${e.clientX}px`;
        ghost.style.top = `${e.clientY}px`;
      }
      const under = document.elementFromPoint(e.clientX, e.clientY);
      const target = under?.closest<HTMLElement>(".gesture-slot, .combo-cell") ?? null;
      if (target !== hoverSlot) {
        clearHover();
        hoverSlot = target;
        hoverSlot?.classList.add("drop-target");
      }
      e.preventDefault();
    };

    /** document / window に付けたリスナーとタイマー・ドラッグ表示をすべて片付ける。 */
    const teardown = (): void => {
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", finish);
      document.removeEventListener("pointercancel", onCancel);
      window.removeEventListener("blur", abort);
      if (longPressTimer !== null) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
      touchDragActive = false;
    };

    /** ドロップもタップ扱いもせずに中断する（pointercancel・ウィンドウのフォーカス喪失・ボタン離しの取りこぼし）。 */
    function abort(): void {
      teardown();
      if (dragging) {
        dragging = false;
        chip.style.touchAction = "";
        try {
          chip.releasePointerCapture(pointerId);
        } catch {
          /* すでに解放済みなら無視 */
        }
        ghost?.remove();
        ghost = null;
      }
      clearHover();
    }

    const onCancel = (e: PointerEvent): void => {
      if (e.pointerId !== pointerId) return;
      abort();
    };

    function finish(e: PointerEvent): void {
      if (e.pointerId !== pointerId) return;
      teardown();
      if (!dragging) {
        // スクロール意図と判定されたあとの離指は何もしない。
        if (canceled) return;
        // ドラッグ判定に達しなかった＝タップ。クリップ単体をプレビューする（解除チップは何もしない）。
        if (clipIndex !== null) {
          store.setPreviewTarget({ kind: "clip", clipIndex });
          refreshPreview();
        }
        return;
      }
      chip.style.touchAction = "";
      try {
        chip.releasePointerCapture(pointerId);
      } catch {
        /* すでに解放済みなら無視 */
      }
      ghost?.remove();
      const dropSlot = hoverSlot;
      clearHover();
      if (dropSlot?.classList.contains("combo-cell")) {
        const left = Number(dropSlot.dataset.left);
        const right = Number(dropSlot.dataset.right);
        store.assignCombo(left, right, clipIndex);
      } else if (dropSlot) {
        const hand = dropSlot.dataset.hand as Hand;
        const gesture = Number(dropSlot.dataset.gesture);
        store.assignGesture(hand, gesture, clipIndex);
      }
    }

    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", finish);
    document.addEventListener("pointercancel", onCancel);
    window.addEventListener("blur", abort);
  });

  return chip;
}

/** クリップトレイの拡大表示状態。再描画をまたいで保持する。 */
let trayExpanded = false;

/** 手のスロットへドラッグして割り当てるクリップチップの一覧（先頭に解除チップ）。横スクロール可能。拡大表示中は複数行で折り返す。 */
function clipTray(): HTMLElement {
  const project = store.project;
  const tray = el("div", { class: `clip-tray${trayExpanded ? " expanded" : ""}` }, clipChip(null, "解除"));
  for (const clip of project?.clips ?? []) tray.appendChild(clipChip(clip.index, clip.name));

  const toggleBtn = el("button", {
    class: "icon-btn tray-expand-btn",
    type: "button",
    text: "⤢",
    "aria-label": "クリップ一覧を拡大",
    "aria-pressed": trayExpanded ? "true" : "false",
    onclick: () => {
      trayExpanded = !trayExpanded;
      store.notify();
    },
  });

  return el(
    "div",
    { class: "clip-tray-row" },
    el("div", { class: `clip-tray-scroll${trayExpanded ? " expanded" : ""}` }, tray),
    toggleBtn,
  );
}

function handBlock(hand: Hand): HTMLElement {
  const grid = el("div", { class: "gesture-grid" });
  for (let g = 0; g < 8; g++) grid.appendChild(slot(hand, g));
  return el("div", { class: "hand-block" }, el("h3", { text: HAND_LABEL[hand] }), grid);
}

/** 左右コンビネーション表（行=左手、列=右手）。横スクロール可能なラッパに入れる。 */
function comboTable(): HTMLElement {
  const table = el("table", { class: "combo-table" });

  const thead = el("thead", {});
  const headRow = el("tr", {}, el("th", { class: "corner", text: "左＼右" }));
  for (let r = 0; r < 8; r++) {
    headRow.appendChild(el("th", { scope: "col", text: `${r}. ${GESTURE_LABELS_JA[r]}` }));
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = el("tbody", {});
  for (let l = 0; l < 8; l++) {
    const row = el("tr", {}, el("th", { scope: "row", text: `${l}. ${GESTURE_LABELS_JA[l]}` }));
    for (let r = 0; r < 8; r++) {
      const clipIndex = store.comboClipIndex(l, r);
      const clip = store.clipByIndex(clipIndex);
      const p = store.previewing;
      const isPreviewing = p?.kind === "combo" && p.left === l && p.right === r;
      const cell = el(
        "td",
        {},
        el("button", {
          class: `combo-cell${clip ? " assigned" : ""}`,
          "aria-pressed": isPreviewing ? "true" : "false",
          "aria-label": `左 ${GESTURE_LABELS_JA[l]} × 右 ${GESTURE_LABELS_JA[r]}: ${clip ? clip.name : "未割り当て"}`,
          title: clip ? clip.name : "未割り当て",
          text: clip ? clip.name : "－",
          "data-left": String(l),
          "data-right": String(r),
          onclick: () => {
            // PC幅はプレビュー更新のみ（割り当てはクリップ一覧からのドラッグ&ドロップ）。
            // プレビューを見ながら選べるようモーダルでプレビューを隠さない。
            // モバイル幅はD&Dがしづらいためタップでモーダルを開く。
            store.setPreviewTarget({ kind: "combo", left: l, right: r });
            refreshPreview();
            if (!isPcLayout()) {
              openClipPicker(`左 ${GESTURE_LABELS_JA[l]} × 右 ${GESTURE_LABELS_JA[r]}`, clipIndex, (ci) =>
                store.assignCombo(l, r, ci),
              );
            }
          },
        }),
      );
      row.appendChild(cell);
    }
    tbody.appendChild(row);
  }
  table.appendChild(tbody);

  return el("div", { class: "combo-scroll" }, table);
}

/** 再描画をまたいで開閉状態を保つ（セルをタップすると再描画が走るため） */
let comboOpen = false;
let menuOpen = false;

/**
 * メニュー/トグル表情（M5, SPEC_FMAPPLY §4.6）。
 * Expressions Menu にトグルとして並び、手の形と無関係に表情を出しっぱなしにできる。
 */
function menuSection(): HTMLElement {
  const project = store.project;
  const entries = project?.menu ?? [];

  const list = el("div", {});
  if (entries.length === 0) {
    list.appendChild(el("p", { class: "muted", text: "まだメニュー表情がありません。「表情を追加」から選んでください。" }));
  }
  entries.forEach((m, i) => {
    const clip = store.clipByIndex(m.clipIndex);
    const isPreviewing = store.previewing?.kind === "menu" && store.previewing.index === m.index;
    const labelInput = el("input", {
      class: "text-input",
      type: "text",
      value: m.label,
      maxlength: "255",
      style: { flex: "1 1 120px", minWidth: "80px" },
    }) as HTMLInputElement;
    labelInput.addEventListener("change", () => {
      const v = labelInput.value.trim();
      if (v.length === 0) {
        labelInput.value = m.label;
        return;
      }
      store.updateMenuEntry(m.index, { label: v });
    });

    const menuBtn = el("button", { class: "icon-btn menu-trigger", text: "⋮", "aria-label": `${m.label} の操作` });
    menuBtn.addEventListener("click", () => {
      void (async () => {
        const choice = await openMenu(
          [
            ...(i > 0 ? [{ label: "上へ", value: "up" }] : []),
            ...(i < entries.length - 1 ? [{ label: "下へ", value: "down" }] : []),
            { label: "削除", value: "delete", danger: true },
          ],
          menuBtn,
        );
        if (choice === "up") store.moveMenuEntry(m.index, -1);
        else if (choice === "down") store.moveMenuEntry(m.index, 1);
        else if (choice === "delete") store.removeMenuEntry(m.index);
      })();
    });

    list.appendChild(
      el(
        "div",
        { class: `clip-item${isPreviewing ? " editing" : ""}` },
        labelInput,
        el("button", {
          class: "btn small",
          text: clip ? clip.name : "表情なし",
          title: "割り当てるクリップを変更",
          onclick: () => {
            store.setPreviewTarget({ kind: "menu", index: m.index });
            refreshPreview();
            openClipPicker(`メニュー「${m.label}」`, m.clipIndex, (ci) => {
              store.updateMenuEntry(m.index, { clipIndex: ci });
              store.setPreviewTarget({ kind: "menu", index: m.index });
              refreshPreview();
            });
          },
        }),
        menuBtn,
      ),
    );
  });

  const details = el(
    "details",
    { class: "advanced", open: menuOpen },
    el(
      "summary",
      {},
      "メニュー表情（トグル）",
      el("span", { class: "notyet", text: entries.length > 0 ? `${entries.length} 件` : "未設定" }),
    ),
    el("p", {
      class: "muted",
      text: "アバターの Expressions Menu に「表情」サブメニューを作り、ここに並べた表情をトグルとして出します。手の形と関係なく表情を固定でき、メニュー表情が ON のあいだはジェスチャーの表情より優先されます。",
    }),
    list,
    el("button", {
      class: "btn block",
      text: "表情を追加",
      style: { marginTop: "10px" },
      disabled: entries.length >= 255,
      onclick: () => {
        openClipPicker("メニューに追加する表情", null, (ci) => {
          store.addMenuEntry(ci);
          refreshPreview();
        });
      },
    }),
  );
  details.addEventListener("toggle", () => {
    menuOpen = details.open;
  });
  return details;
}

function comboSection(): HTMLElement {
  const count = store.project?.combos.filter((c) => c.clipIndex !== null).length ?? 0;
  const details = el(
    "details",
    { class: "advanced", open: comboOpen },
    el(
      "summary",
      {},
      "詳細（左右コンビネーション）",
      el("span", { class: "notyet", text: count > 0 ? `${count} 件` : "未設定" }),
    ),
    el("p", {
      class: "muted",
      text: "左右の手の組み合わせに表情を割り当てます。ここで設定した組み合わせは、上の基本の左右割り当てよりも優先されます（両手がその形になったときはコンビネーション側の表情が出ます）。",
    }),
    el("p", {
      class: "muted",
      text: isPcLayout()
        ? "上のクリップ一覧からセルへドラッグすると割り当てられます。セルをタップするとプレビューを確認できます。表は横にスクロールできます。"
        : "セルをタップすると割り当てるクリップを選べます。上のクリップ一覧からセルへドラッグしても割り当てられます。表は横にスクロールできます。",
    }),
    comboTable(),
    count > 0
      ? el("button", {
          class: "btn ghost block",
          text: "コンビネーションをすべて解除",
          style: { marginTop: "10px" },
          onclick: () => {
            if (!store.project) return;
            for (const c of [...store.project.combos]) store.assignCombo(c.left, c.right, null);
            store.setPreviewTarget(null);
            refreshPreview();
          },
        })
      : null,
  );
  details.addEventListener("toggle", () => {
    comboOpen = details.open;
  });
  return details;
}

export function renderGestureView(): HTMLElement {
  const root = el("div", { class: "view-split" });
  attachPreview(root);
  if (!store.avatar) return root;

  const panel = el("div", { class: "view-panel" });
  root.appendChild(panel);

  const previewing = store.previewing;
  const previewClip = store.previewingClip();
  const previewLabel = !previewing
    ? "スロットをタップするとプレビューを確認できます。下のクリップをスロットへドラッグすると割り当てられます。"
    : previewing.kind === "hand"
      ? `プレビュー中: ${HAND_LABEL[previewing.hand]} ${GESTURE_LABELS_JA[previewing.gesture]} → ${previewClip ? previewClip.name : "割り当てなし"}`
      : previewing.kind === "combo"
        ? `プレビュー中: 左 ${GESTURE_LABELS_JA[previewing.left]} × 右 ${GESTURE_LABELS_JA[previewing.right]} → ${previewClip ? previewClip.name : "割り当てなし"}`
        : previewing.kind === "menu"
          ? `プレビュー中: メニュー「${store.project?.menu.find((m) => m.index === previewing.index)?.label ?? ""}」 → ${previewClip ? previewClip.name : "表情なし"}`
          : `プレビュー中: クリップ「${previewClip ? previewClip.name : ""}」`;

  panel.appendChild(
    el(
      "div",
      { class: "section" },
      el("p", { class: "muted", text: previewLabel }),
      clipTray(),
      handBlock("left"),
      handBlock("right"),
      comboSection(),
      menuSection(),
    ),
  );
  return root;
}
